import { getWeekStartForDate, type MealType, type PlannedMeal } from '../redux/mealPlanSlice';
import type { SlotAction } from '../components/recipe-detail/planSheetLogic';

/**
 * Turning "the user tapped Thursday and chose Échanger" into dispatches.
 *
 * Both the planner and the recipe detail screen open the same plan sheet, so
 * both have to answer the same question, and they used to answer it with two
 * hand-written handlers that had already drifted — one swapped whole slots, the
 * other stacked. Deriving it once here keeps the promise on the button and the
 * mutation that follows from parting ways.
 */

/** What the sheet resolved a tap to. `'move'` is the empty-slot case. */
export type MoveAction = 'move' | SlotAction;

export interface SlotAddress {
  scheduledFor: string;
  mealType: MealType;
}

export interface MovePlan {
  action: MoveAction;
  source: SlotAddress;
  target: SlotAddress;
  /** Monday of the week the dish leaves. */
  sourceWeekStart: string;
  /** Monday of the week the dish lands in. */
  targetWeekStart: string;
  /** True when the dish changes week — two buckets to sync, two to snapshot. */
  crossWeek: boolean;
  /** Every week the mutation rewrites, deduped: what undo must snapshot. */
  affectedWeeks: string[];
}

export function buildMovePlan(
  meal: PlannedMeal,
  target: SlotAddress,
  action: MoveAction,
): MovePlan | null {
  const source: SlotAddress = { scheduledFor: meal.scheduledFor, mealType: meal.mealType };
  if (source.scheduledFor === target.scheduledFor && source.mealType === target.mealType) {
    return null;
  }

  const sourceWeekStart = getWeekStartForDate(source.scheduledFor);
  const targetWeekStart = getWeekStartForDate(target.scheduledFor);
  const crossWeek = sourceWeekStart !== targetWeekStart;

  return {
    action,
    source,
    target,
    sourceWeekStart,
    targetWeekStart,
    crossWeek,
    affectedWeeks: crossWeek ? [sourceWeekStart, targetWeekStart] : [sourceWeekStart],
  };
}

/**
 * Monday-first day index (0 = Monday) of a date, for the "moved to {{day}}"
 * toast. Read from the local date fields — `getDay()` on a bare `YYYY-MM-DD`
 * parses as UTC midnight and lands on the previous day west of Greenwich.
 */
export function dayIndexOf(isoDate: string): number {
  return (new Date(isoDate + 'T12:00:00').getDay() + 6) % 7;
}
